import { useState, useEffect, useContext } from 'react';
import StaffNav from './managernav';
import { AuthContext } from '../AuthContext';
import { useNavigate } from 'react-router-dom';

export default function NewUserForm() {
  const [formData, setFormData] = useState({
    Name: '',
    Email: '',
    Telephone: '',
    EmergencyContact: '',
    EmergencyName: '',
    StreetAddress: '',
    City: '',
    County: '',
    PostCode: '',
    HourlyPay: '',
    Country: '',
    ContractType: '',
    DOB: '',
    Address: '',
    Position: '',
    Department: '',
    Hours: '',
    Location: '',
    ManagementLevel: '',
    StartDate: '',
    NationalInsuranceNumber: '',
    Medical: '',
    DaysPerWeek: '',
    newStaff: '1',
    Probation: '',
    EmployeeID: '',
  });
  const [roles, setRoles] = useState([]);
  const [team, setTeam] = useState([]);
  const [companyLocation, setCompanyLocation] = useState('');
  const [showProbationInput, setShowProbationInput] = useState(false);
  const [probationPeriod, setProbationPeriod] = useState(0);

  const navigate = useNavigate();
  const user = useContext(AuthContext);
  const normalizeManagementLevel = (value) => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= 1 && parsed <= 4 ? parsed : 1;
  };
  const deriveAccessFromManagementLevel = (managementLevel) => {
    if (managementLevel >= 4) return 'admin';
    if (managementLevel > 1) return 'manager';
    return 'staff';
  };

  console.log(formData);

  // Fetch company location
  useEffect(() => {
    const getCompany = async () => {
      try {
        const response = await fetch("https://api.insightos.co.uk/api/company", {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to fetch company');
        const data = await response.json();
        setCompanyLocation(data.data[0].City);
      } catch (error) {
        console.error('Error fetching company:', error);
      }
    };
    getCompany();
  }, []);

  // Fetch roles
  useEffect(() => {
    const getRoles = async () => {
      try {
        const response = await fetch("https://api.insightos.co.uk/api/allroles", {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to fetch roles');
        const data = await response.json();
        setRoles(data.data);
      } catch (error) {
        console.error('Error fetching roles:', error);
      }
    };
    getRoles();
  }, []);

  // Fetch departments when Position changes
  useEffect(() => {
    const getDepartments = async () => {
      try {
        const response = await fetch("https://api.insightos.co.uk/api/getdepartments", {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: 'include',
        });
        if (!response.ok) throw new Error("Failed to fetch departments");
        const data = await response.json();
        setTeam(data.data);
      } catch (error) {
        console.error("Error fetching departments:", error);
      }
    };
    getDepartments();
  }, [formData.Position]);

  // Fetch probation period once
  useEffect(() => {
    const getProbation = async () => {
      try {
        const response = await fetch("https://api.insightos.co.uk/api/policies", {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to fetch policies');
        const data = await response.json();
        setProbationPeriod(data.data[0].ProbationPeriod || 0);
      } catch (error) {
        console.error('Error fetching policies:', error);
      }
    };
    getProbation();
  }, []);

  // Set HourlyPay when Position changes
  useEffect(() => {
    const role = roles.find((role) => role.Name === formData.Position);
    if (role) {
      setFormData((prevData) => ({
        ...prevData,
        HourlyPay: role.PayRate,
      }));
    }
  }, [formData.Position, roles]);

  // Get selected role information
  const getSelectedRoleInfo = () => {
    return roles.find((role) => role.Name === formData.Position) || null;
  };
  const getSelectedRoleManagementLevel = () => {
    return normalizeManagementLevel(getSelectedRoleInfo()?.ManagementLevel);
  };
  const getSelectedRoleAccess = () => {
    const normalizedAccess = String(getSelectedRoleInfo()?.Access || '').trim().toLowerCase();
    if (['admin', 'manager', 'staff'].includes(normalizedAccess)) {
      return normalizedAccess;
    }
    return deriveAccessFromManagementLevel(getSelectedRoleManagementLevel());
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle newStaff change
  const handleNewStaffChange = (e) => {
    const { value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      newStaff: value,
      Probation: value === '1' ? prevData.Probation : '', // reset if not new staff
    }));
    setShowProbationInput(value !== '1');
  };

  // Calculate probation end date when newStaff or StartDate changes
  useEffect(() => {
    if (formData.newStaff === '1' && formData.StartDate && probationPeriod > 0) {
      const startDate = new Date(formData.StartDate);
      if (!isNaN(startDate)) {
        const probationEndDate = new Date(startDate);
        probationEndDate.setMonth(startDate.getMonth() + probationPeriod);
        const formattedDate = probationEndDate.toISOString().split('T')[0];
        setFormData((prevData) => ({
          ...prevData,
          Probation: formattedDate,
        }));
      }
    }
    // If not new staff, don't auto-calculate
    if (formData.newStaff !== '1') {
      setFormData((prevData) => ({
        ...prevData,
        Probation: '',
      }));
    }
    // eslint-disable-next-line
  }, [formData.newStaff, formData.StartDate, probationPeriod]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const selectedRoleInfo = getSelectedRoleInfo();
    const resolvedManagementLevel = formData.ManagementLevel
      ? normalizeManagementLevel(formData.ManagementLevel)
      : normalizeManagementLevel(selectedRoleInfo?.ManagementLevel);
    const resolvedAccess = ['admin', 'manager', 'staff'].includes(String(selectedRoleInfo?.Access || '').trim().toLowerCase())
      ? String(selectedRoleInfo?.Access).trim().toLowerCase()
      : deriveAccessFromManagementLevel(resolvedManagementLevel);
    const dataForm = {
      Name: formData.Name,
      CompanyCode: user.companyCode,
      Email: formData.Email,
      Telephone: formData.Telephone,
      EmergencyContact: formData.EmergencyName + ' ' + formData.EmergencyContact,
      Address: formData.StreetAddress + ', ' + formData.City + ', ' + formData.County + ', ' + formData.PostCode + ', ' + formData.Country,
      DOB: formData.DOB,
      Role: formData.Position,
      Country: formData.Country,
      ManagementLevel: resolvedManagementLevel,
      Access: resolvedAccess,
      HourlyPay: formData.HourlyPay,
      StartDate: formData.StartDate,
      NationalInsuranceNumber: formData.NationalInsuranceNumber,
      Location: formData.Location,
      Medical: formData.Medical,
      ContractType: formData.ContractType,
      ContractedHours: formData.Hours,
      ContractedDays: formData.DaysPerWeek,
      Department: formData.Department,
      Probation: formData.Probation, // always use the value from formData
      EmployeeID: formData.EmployeeID,
    };
    try {
      const response = await fetch('https://api.insightos.co.uk/api/createuser', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataForm),
      });
      if (response.ok) {
        navigate('/users');
      } else {
        console.error('Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <StaffNav />
      <div className="max-w-4xl mx-auto mt-10 bg-white rounded-xl shadow-lg p-8">
        <h1 className="text-2xl font-bold text-indigo-700 mb-6">Add New Employee</h1>
        <form onSubmit={handleSubmit} className="space-y-10">
          {/* Personal Information */}
          <section>
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Personal Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Full Name</label>
                <input name="Name" value={formData.Name} onChange={handleChange} type="text" required className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                <input name="DOB" value={formData.DOB} onChange={handleChange} type="date" required className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Work Email Address</label>
                <input name="Email" value={formData.Email} onChange={handleChange} type="email" required className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Telephone</label>
                <input name="Telephone" value={formData.Telephone} onChange={handleChange} type="text" required className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">National Insurance Number</label>
                <input name="NationalInsuranceNumber" value={formData.NationalInsuranceNumber} onChange={handleChange} type="text" className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Emergency Contact Name</label>
                <input name="EmergencyName" value={formData.EmergencyName} onChange={handleChange} type="text" className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Emergency Contact Number</label>
                <input name="EmergencyContact" value={formData.EmergencyContact} onChange={handleChange} type="text" className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
              </div>
            </div>
          </section>

          {/* Address */}
          <section>
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Address</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Street Address</label>
                <input name="StreetAddress" value={formData.StreetAddress} onChange={handleChange} type="text" className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">City</label>
                <input name="City" value={formData.City} onChange={handleChange} type="text" className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">County</label>
                <input name="County" value={formData.County} onChange={handleChange} type="text" className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Post Code</label>
                <input name="PostCode" value={formData.PostCode} onChange={handleChange} type="text" className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Country</label>
                <select name="Country" value={formData.Country} onChange={handleChange} className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500">
                  <option value="">Select a country</option>
                  <option value="England">England</option>
                  <option value="Wales">Wales</option>
                  <option value="Scotland">Scotland</option>
                  <option value="Northern Ireland">Northern Ireland</option>
                </select>
              </div>
            </div>
          </section>

          {/* Job Information */}
          <section>
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Job Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Employee ID</label>
                <input name="EmployeeID" value={formData.EmployeeID} onChange={handleChange} type="text" className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Start Date</label>
                <input name="StartDate" value={formData.StartDate} onChange={handleChange} type="date" required className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">New Staff Member</label>
                <select name="newStaff" value={formData.newStaff} onChange={handleNewStaffChange} className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500">
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>
              </div>
              {showProbationInput && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Probation End Date</label>
                  <input
                    name="Probation"
                    value={formData.Probation}
                    onChange={handleChange}
                    type="date"
                    required
                    className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
              )}
              {!showProbationInput && formData.Probation && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Probation End Date</label>
                  <input
                    name="Probation"
                    value={formData.Probation}
                    type="date"
                    readOnly
                    className="mt-1 w-full rounded-md border-gray-200 bg-gray-100 shadow-sm"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700">Management Level</label>
                <select name="ManagementLevel" value={formData.ManagementLevel} onChange={handleChange} className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500">
                  <option value="">Select a management level</option>
                  <option value="1">Employee</option>
                  <option value="2">Manager</option>
                  <option value="3">Higher Management</option>
                  <option value="4">Director/C-Suite Level</option>
                </select>
                {getSelectedRoleInfo() && (
                  <div className="mt-2 text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-md border">
                    Role default: level {getSelectedRoleManagementLevel()}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Location</label>
                <select name="Location" value={formData.Location} onChange={handleChange} className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500">
                  <option value="">Select a location</option>
                  <option value="Remote">Remote</option>
                  <option value="Hybrid">Hybrid</option>
                  {companyLocation && <option value={companyLocation}>{companyLocation}</option>}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <select name="Position" value={formData.Position} onChange={handleChange} className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500">
                  <option value="">Select a role</option>
                  {roles.map((role) => (
                    <option key={role.Name} value={role.Name}>{role.Name}</option>
                  ))}
                </select>
                {getSelectedRoleInfo() && (
                  <div className="mt-2 text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-md border">
                    Role default access: {getSelectedRoleAccess()}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Department</label>
                <select name="Department" value={formData.Department} onChange={handleChange} className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500">
                  <option value="">Select a department</option>
                  {team.map((department) => (
                    <option key={department.Name} value={department.Name}>{department.Name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Contract Type</label>
                <select name="ContractType" value={formData.ContractType} onChange={handleChange} className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500">
                  <option value="">Select a contract type</option>
                  <option value="Full-Time">Full Time</option>
                  <option value="0 Hours">0 Hours Contract</option>
                  <option value="Part-Time">Part Time</option>
                </select>
                {getSelectedRoleInfo() && (
                    <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-md border">
                      Role default: {getSelectedRoleInfo().ContractType}
                    </div>
                  )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Contracted Hours</label>
                <div className="flex items-center space-x-3">
                  <input name="Hours" value={formData.Hours} onChange={handleChange} type="number" className="mt-1 flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
                  {getSelectedRoleInfo() && (
                    <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-md border">
                      Role default: {getSelectedRoleInfo().ContractedHours} hours
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Contracted Days (Weekly)</label>
                <div className="flex items-center space-x-3">
                  <input name="DaysPerWeek" value={formData.DaysPerWeek} onChange={handleChange} type="number" max={7} className="mt-1 flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
                  {getSelectedRoleInfo() && (
                    <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-md border">
                      Role default: {getSelectedRoleInfo().ContractedDays} days
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Medical Information</label>
                <input name="Medical" value={formData.Medical} onChange={handleChange} type="text" className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Hourly Pay</label>
                <div className="flex items-center space-x-3">
                  <input name="HourlyPay" value={formData.HourlyPay} readOnly className="mt-1 flex-1 rounded-md border-gray-200 bg-gray-100 shadow-sm" />
                  {getSelectedRoleInfo() && (
                    <div className="text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-md border border-blue-200">
                      Role rate: £{getSelectedRoleInfo().PayRate} ({getSelectedRoleInfo().PayType})
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

          <div className="flex justify-end">
            <button
              type="submit"
              data-component="new-user-form"
              
              data-feature="create_employee_post"
              data-time-saved="2"
              className="inline-flex items-center px-6 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Save Employee
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
